import { SiteSummary } from '../services/api';

interface SiteCardProps {
  site: SiteSummary;
  onClick: () => void;
}

function formatNumber(num: number): string {
  return num.toLocaleString();
}

function formatPercent(num: number): string {
  return `${(num * 100).toFixed(1)}%`;
}

function getUtilizationColor(utilization: number): string {
  if (utilization < 0.7) return '#22c55e'; // Green - opportunity
  if (utilization > 0.95) return '#ef4444'; // Red - near full
  return '#3b82f6'; // Blue - normal
}

function getUtilizationLabel(utilization: number): string {
  if (utilization < 0.7) return 'Open Capacity';
  if (utilization > 0.95) return 'Near Full';
  return '';
}

export default function SiteCard({ site, onClick }: SiteCardProps) {
  const utilizationColor = getUtilizationColor(site.totalUtilization);
  const utilizationLabel = getUtilizationLabel(site.totalUtilization);

  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '1.5rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, transform 0.2s',
        border: `2px solid ${utilizationColor}20`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>{site.name}</h3>
          <p style={{ margin: '0.25rem 0 0', color: '#666', fontSize: '0.875rem' }}>{site.location}</p>
        </div>
        {utilizationLabel && (
          <span
            style={{
              backgroundColor: `${utilizationColor}20`,
              color: utilizationColor,
              padding: '0.25rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.75rem',
              fontWeight: 500,
            }}
          >
            {utilizationLabel}
          </span>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <div
          style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            border: `4px solid ${utilizationColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '1rem',
          }}
        >
          <span style={{ fontSize: '1rem', fontWeight: 600, color: utilizationColor }}>
            {formatPercent(site.totalUtilization)}
          </span>
        </div>
        <div>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>Utilization</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div>
          <p style={{ margin: 0, fontSize: '0.75rem', color: '#666', textTransform: 'uppercase' }}>
            Planned Cases
          </p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '1rem', fontWeight: 500 }}>
            {formatNumber(site.totalPlannedCases)}
          </p>
        </div>
        <div>
          <p style={{ margin: 0, fontSize: '0.75rem', color: '#666', textTransform: 'uppercase' }}>
            Open Capacity
          </p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '1rem', fontWeight: 500 }}>
            {formatNumber(site.totalOpenCapacity)}
          </p>
        </div>
      </div>
    </div>
  );
}
